'use client';

import { useState } from 'react';
import { usePublicEducationLists } from '@/api/usePublicEducationLists';
import { EducationCard } from '@/modules/education/components/EducationCard';
import { Button } from '@/components/ui/button';
import PagesHeader from '@/components/PagesHeader/PagesHeader';
import { PublicSignUpDialog } from '@/components/public/PublicSignupDialog';
import { useRouter } from 'next/navigation';
import { Education } from '@/types/education';

export default function PublicEducationClient({
  initialEducations,
}: {
  initialEducations: { responseObject: { rows: Education[]; count: number } } | null;
}) {
  const router = useRouter();
  const [searchText, setSearchText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [showPublicSignupDialog, setShowPublicSignupDialog] = useState(false);

  const { data, isLoading, isError } = usePublicEducationLists(initialEducations);

  const educationItems =
    data?.pages.flatMap((page) => page.responseObject?.rows || []) || [];
  const displayItems = educationItems.slice(0, 8);

  const handleCategoryChange = (categoryId: string) => {
    setSelectedCategory(categoryId);
  };

  const handleEducationClick = (
    education: Education,
    e: React.MouseEvent,
    index: number
  ) => {
    e.preventDefault();
    e.stopPropagation();
    
    sessionStorage.setItem(
      `education-${education.id}`,
      JSON.stringify(education)
    );
    
    if (index === 0) {
      router.push(`/public/education/${education.id}`);
    } else {
      setShowPublicSignupDialog(true);
    }
  };

  return (
    <div className="min-h-screen">
      <PagesHeader
        searchText={searchText}
        setSearchText={setSearchText}
        searchPlaceholder="Search Education..."
        tabs={[
          {
            label: 'All Education',
            href: '/public/education',
          },
          {
            label: 'Your Education',
            href: '#',
          },
          {
            label: 'Saved Education',
            href: '#',
          },
        ]}
        activeTab="/public/education"
        showAllFilters={true}
        categories={['']}
        selectedCategory={selectedCategory}
        setSelectedCategory={handleCategoryChange}
      />

      <div className="flex flex-wrap gap-5 mt-6">
        {isLoading ? (
          Array.from({ length: 8 }).map((_, index) => (
            <div key={index} className="animate-pulse">
              <div className="bg-gray-200 h-48 rounded-lg mb-4 w-64"></div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-200 rounded w-16"></div>
                <div className="h-4 bg-gray-200 rounded w-3/4"></div>
              </div>
            </div>
          ))
        ) : isError ? (
          <div className="col-span-full text-center py-8">
            Error loading education data.
          </div>
        ) : displayItems.length === 0 ? (
          <div className="col-span-full text-center py-8">
            No schools found.
          </div>
        ) : (
          displayItems.map((education, index) => {
            const isClickable = index === 0;
            return (
              <div
                key={`${education.id}-${index}`}
                className={`animate-in fade-in-0 slide-in-from-bottom-4 duration-500 ${
                  isClickable ? 'cursor-pointer' : 'cursor-default'
                }`}
                style={{
                  animationDelay: `${Math.min(index * 100, 1000)}ms`,
                  animationFillMode: 'both',
                }}
                onClick={(e) => handleEducationClick(education, e, index)}
              >
                <EducationCard
                  education={education}
                  index={index}
                  isClickable={isClickable}
                />
              </div>
            );
          })
        )}
      </div>

      {educationItems.length > 8 && (
        <div className="text-center my-8 py-8 px-4">
          <div className="max-w-md mx-auto">
            <h3 className="text-2xl font-bold mb-2">👋 See More Schools!</h3>
            <p className="text-gray-600 mb-6">
              You have viewed 8 schools. Join our community to explore all
              options!
            </p>
            <Button
              onClick={() => setShowPublicSignupDialog(true)}
              className="bg-[#948780] hover:bg-[#7a6b64] text-black px-8 py-4 rounded-full font-bold"
            >
              Sign Up
            </Button>
          </div>
        </div>
      )}

      <PublicSignUpDialog
        open={showPublicSignupDialog}
        onOpenChange={setShowPublicSignupDialog}
        message="Sign up to explore schools, daycares, and educational resources in your community!"
      />
    </div>
  );
}
